import { Avatar, Link, Typography } from '@/ui';

import type { Session } from '@/types';
import type { FC, ReactNode } from 'react';

const ParticipantLink: FC<{
  avatarUrl?: string;
  children: ReactNode;
  firstName?: string;
  href: string;
  label: string;
  lastName?: string;
  name: string;
}> = ({ avatarUrl, children, firstName, href, label, lastName, name }) => (
  <Link
    aria-label={name}
    className="flex min-h-[77px] flex-1 items-center gap-3 rounded-md bg-cement-100 px-5 py-2 text-cement-500 no-underline"
    href={href}
    rel="noopener noreferrer"
    target="_blank"
  >
    <Avatar
      firstName={firstName}
      lastName={lastName}
      size="s"
      src={avatarUrl}
    />
    <div className="min-w-0">
      <Typography className="truncate text-sm font-medium">{label}</Typography>
      <Typography className="truncate text-xs text-cement-400">
        {children}
      </Typography>
    </div>
  </Link>
);

const CoachSessionParticipants: FC<{
  memberSecondary?: string;
  session: Session;
}> = ({ memberSecondary, session }) => {
  const memberContent = (
    <>
      <Avatar
        firstName={session.user.firstName}
        lastName={session.user.lastName}
        size="s"
        src={session.user.avatarUrl}
      />
      <div className="min-w-0">
        <Typography className="truncate text-sm font-medium">
          {session.user.fullName}
        </Typography>
        <Typography className="truncate text-xs text-cement-400">
          {memberSecondary || 'Member'}
        </Typography>
      </div>
    </>
  );

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-cement-200 bg-white p-2"
      data-testid="coach-session-participants"
    >
      <div className="flex flex-col gap-1 sm:flex-row">
        <ParticipantLink
          avatarUrl={session.coach.avatarUrl}
          firstName={session.coach.firstName}
          href="/coach/profile"
          label={session.coach.fullName}
          lastName={session.coach.lastName}
          name={session.coach.fullName}
        >
          Coach
        </ParticipantLink>
        {session.user.id ? (
          <ParticipantLink
            avatarUrl={session.user.avatarUrl}
            firstName={session.user.firstName}
            href={`/coach/members/${session.user.id}`}
            label={session.user.fullName}
            lastName={session.user.lastName}
            name={session.user.fullName}
          >
            {memberSecondary || 'Member'}
          </ParticipantLink>
        ) : (
          <div className="flex min-h-[77px] flex-1 items-center gap-3 rounded-md bg-cement-100 px-5 py-2 text-cement-500">
            {memberContent}
          </div>
        )}
      </div>
      <Typography className="text-base font-medium">{session.title}</Typography>
    </div>
  );
};

export default CoachSessionParticipants;
